// Payment Manager — shell that routes 4 tabs + S6 kill-switch wrapper.
//
// Every tab lives in its own file under ./PaymentManager/. This page is pure
// composition: header + tab bar + tab routing. An emergency kill-switch
// (feature flag key `payment_manager_emergency_disable`) short-circuits the
// entire surface with a maintenance banner — intended as a break-glass tool
// if a downstream payment provider outage should hide the admin UI.

import { useState, useCallback } from "react";
import {
  CreditCard,
  Smartphone,
  ShieldCheck,
  BarChart3,
  AlertTriangle,
} from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import { useAdminPageTitle } from "@/hooks/admin/useAdminPageTitle";
import { useFeatureFlags } from "@/hooks/admin/useEnterprise";
import type { Tab, TabId } from "./PaymentManager/constants";
import { TransactionsTab } from "./PaymentManager/TransactionsTab";
import { MobileMoneyTab } from "./PaymentManager/MobileMoneyTab";
import { CODTrustTab } from "./PaymentManager/CODTrustTab";
import { AnalyticsTab } from "./PaymentManager/AnalyticsTab";

/**
 * Safeguard S6: emergency kill-switch for the refactored PaymentManager.
 * If the `payment_manager_emergency_disable` feature flag is enabled, the
 * entire surface hides behind a maintenance banner. Flip the flag off in
 * Supabase to restore.
 */
function EmergencyDisabledBanner() {
  return (
    <AdminLayout>
      <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-amber-200 bg-amber-50 py-16 px-6 text-center">
        <AlertTriangle className="h-8 w-8 text-amber-600" />
        <h1 className="font-display text-xl font-semibold text-stone-900">
          Paiements temporairement indisponibles
        </h1>
        <p className="max-w-md text-sm text-stone-600">
          La gestion des paiements est en maintenance. Merci de réessayer
          dans quelques minutes.
        </p>
      </div>
    </AdminLayout>
  );
}
export default function PaymentManager() {
  useAdminPageTitle("Paiements");
  const { data: flags } = useFeatureFlags();
  const [activeTab, setActiveTab] = useState<TabId>("transactions");

  const killSwitch =
    flags?.flags.some(
      (f) => f.key === "payment_manager_emergency_disable" && f.enabled,
    ) ?? false;

  const handleTabChange = useCallback((id: TabId) => {
    setActiveTab(id);
  }, []);

  if (killSwitch) return <EmergencyDisabledBanner />;

  const tabs: readonly Tab[] = [
    { id: "transactions", label: "Transactions", icon: <CreditCard className="h-4 w-4" /> },
    { id: "mobile_money", label: "Mobile Money", icon: <Smartphone className="h-4 w-4" /> },
    { id: "cod_trust", label: "Score COD", icon: <ShieldCheck className="h-4 w-4" /> },
    { id: "analytics", label: "Analytiques", icon: <BarChart3 className="h-4 w-4" /> },
  ];

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page header */}
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
            <CreditCard className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-display text-xl font-semibold text-stone-900">
              Paiements
            </h1>
            <p className="text-sm text-stone-500">
              Gérez les transactions, remboursements et litiges
            </p>
          </div>
        </div>

        {/* Tab bar */}
        <div
          role="tablist"
          aria-label="Onglets paiements"
          className="flex gap-1 rounded-lg border border-stone-200 bg-stone-50 p-1"
        >
          {tabs.map((tab) => (
            <button
              key={tab.id}
              role="tab"
              aria-selected={activeTab === tab.id}
              type="button"
              onClick={() => handleTabChange(tab.id)}
              className={`flex flex-1 items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? "bg-white text-stone-900 shadow-sm"
                  : "text-stone-500 hover:text-stone-700"
              }`}
            >
              {tab.icon}
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Tab content */}
        {activeTab === "transactions" && <TransactionsTab />}
        {activeTab === "mobile_money" && <MobileMoneyTab />}
        {activeTab === "cod_trust" && <CODTrustTab />}
        {activeTab === "analytics" && <AnalyticsTab />}
      </div>
    </AdminLayout>
  );
}
